import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { userHasTaskTimerAccess } from "@lifeat/shared/user/UserUtil";
import { ALL_TIMER_MODE_TYPE, TimerModeType } from "@lifeat/shared/widget/PomodoroTimerTypes";
import LifeAtDropdownSelector from "@common/LifeAtDropdownSelector/LifeAtDropdownSelector";
import LifeAtDropdownSelectorElement from "@common/LifeAtDropdownSelector/LifeAtDropdownSelectorElement";
import { useAppearanceContext } from "@contexts/AppearanceContext";
import { useCallToActionContext } from "@contexts/CallToActionContext";
import { TimerVolumeContextProvider } from "@contexts/TimerVolumeContext";
import { useUserContext } from "@contexts/UserContext";
import PomodoroIcon from "@icons/PomodoroIcon.svg";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { SignUpCTASource } from "@user/AmplitudeUserEvents";
import { taskTimerState$ } from "../../../../task-management/TaskTimerGlobals";
import Widget, { WidgetProps } from "../Widget";
import GuestPomodoro from "./GuestPomodoro";
import HostPomodoro from "./HostPomodoro";
import PersonalPomodoro from "./PersonalPomodoro";
import SkeletonPomodoro from "./SkeletonPomodoro";
import TaskTimer from "./TaskTimer";
import { GROUP_TIMER_LABEL, PERSONAL_TIMER_LABEL, TASK_TIMER_LABEL } from "./TimerWidgetConstants";

const PomodoroCountHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
`;

const TimerDropdown = styled(LifeAtDropdownSelector)`
    margin-right: 20px;
    & .dropdown-selector-preview {
        padding: 0;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 240px;
    }
`;

const TimerDropdownItem = styled.span<{ active: boolean }>`
    padding: 8px;
    color: ${({ theme, active }) => (active ? theme.textColorSpecial : theme.textColorPrimary)};
`;

const PomoActiveIcon = styled(PomodoroIcon)`
    margin-right: 5px;
    fill: #efa18f;
`;

const PomoInactiveIcon = styled(PomodoroIcon)`
    margin-right: 5px;
    fill: lightgrey;
`;

// The number of pomodoro sessions a user needs to complete before taking a long break.
// Note: this will be a user-configurable setting in the future (see Pomodoro.tsx).
const NUM_POMOS_BEFORE_LONG_BREAK = 4;

type TimerWidgetProps = WidgetProps;
export default function TimerWidget(props: TimerWidgetProps) {
    const { lightMode } = useAppearanceContext();
    const { setSignInView, setUpgradeCtaContext } = useCallToActionContext();
    const { user, userData } = useUserContext();
    const { roomId } = useParams<{ roomId?: string }>();
    const isHost = roomId ? isRoomOwnedByUser(roomId, userData) : false;

    const [personalPomodoroCount, setPersonalPomodoroCount] = useState(0);
    const [hidePersonalPomodoroCount, setHidePersonalPomodoroCount] = useState(false);
    const [groupPomodoroCount, setGroupPomodoroCount] = useState(0);
    const [hideGroupPomodoroCount, setHideGroupPomodoroCount] = useState(false);

    const [selectedTimer, setSelectedTimer] = useState<TimerModeType>("personal");

    const timerModeLabel: Record<TimerModeType, string> = {
        personal: PERSONAL_TIMER_LABEL,
        group: GROUP_TIMER_LABEL,
        task: (taskTimerState$.value.isTimerRunning && taskTimerState$.value.taskTitle) || TASK_TIMER_LABEL,
    };

    const numTomato = useMemo(() => {
        switch (selectedTimer) {
            case "personal":
                return personalPomodoroCount % NUM_POMOS_BEFORE_LONG_BREAK;
            case "group":
                return groupPomodoroCount % NUM_POMOS_BEFORE_LONG_BREAK;
            case "task":
                return 0;
        }
    }, [selectedTimer, personalPomodoroCount, groupPomodoroCount]);
    const shouldShowTomato =
        (selectedTimer === "personal" && !hidePersonalPomodoroCount) ||
        (selectedTimer === "group" && !hideGroupPomodoroCount);

    const updateSelectedTimerMode = async (mode: TimerModeType) => {
        if (mode === "task" && !userHasTaskTimerAccess(userData, user?.metadata?.creationTime)) {
            setUpgradeCtaContext("taskTimer", UpgradeCTASource.TASK_TIMER);
        } else if (user) {
            setSelectedTimer(mode);
        } else {
            if (mode === "group") {
                setSignInView(SignUpCTASource.GROUP_POMODORO);
            }
        }
    };

    const handleGuestPomodoroIsRunningChange = (isRunning: boolean) => {
        if (isRunning) {
            setSelectedTimer("group");
            props.openWidget?.();
        }
    };

    const header = (
        <PomodoroCountHeader>
            <TimerDropdown selectedElementId={selectedTimer} selectedElementName={timerModeLabel[selectedTimer]}>
                {ALL_TIMER_MODE_TYPE.map((mode) => {
                    return (
                        <LifeAtDropdownSelectorElement key={mode} handleSelect={() => updateSelectedTimerMode(mode)}>
                            <TimerDropdownItem active={selectedTimer === mode}>
                                {timerModeLabel[mode]}
                            </TimerDropdownItem>
                        </LifeAtDropdownSelectorElement>
                    );
                })}
            </TimerDropdown>
            {shouldShowTomato &&
                Array(NUM_POMOS_BEFORE_LONG_BREAK)
                    .fill(null)
                    .map((_, i) => (i < numTomato ? <PomoActiveIcon key={i} /> : <PomoInactiveIcon key={i} />))}
        </PomodoroCountHeader>
    );

    return (
        <Widget headerContent={header} lightMode={lightMode} {...props}>
            <TimerVolumeContextProvider>
                {user && userData ? (
                    <PersonalPomodoro
                        hidden={selectedTimer !== "personal"}
                        updateCompletedPomos={(numComplete: number) => setPersonalPomodoroCount(numComplete)}
                        updateHidePomodoroCount={(value: boolean) => setHidePersonalPomodoroCount(value)}
                        user={user}
                        userData={userData}
                    />
                ) : (
                    <SkeletonPomodoro hidden={selectedTimer !== "personal"} />
                )}
                {isHost ? (
                    <HostPomodoro
                        hidden={selectedTimer !== "group"}
                        updateCompletedPomos={(numComplete: number) => setGroupPomodoroCount(numComplete)}
                        updateHidePomodoroCount={(value: boolean) => setHideGroupPomodoroCount(value)}
                    />
                ) : (
                    <GuestPomodoro
                        hidden={selectedTimer !== "group"}
                        updateCompletedPomos={(numComplete: number) => setGroupPomodoroCount(numComplete)}
                        onIsRunningChange={handleGuestPomodoroIsRunningChange}
                    />
                )}
                {user && userData && (
                    <TaskTimer
                        hidden={selectedTimer !== "task"}
                        openTimerWidget={props.openWidget}
                        setTimerModeToTask={() => setSelectedTimer("task")}
                    />
                )}
            </TimerVolumeContextProvider>
        </Widget>
    );
}
